/**
 * Вход по лицу — the browser's version of the iOS app's Face ID.
 *
 * **Not the same mechanism, deliberately.** On iOS, biometrics unlocks a token already in the
 * Keychain: the check is local and the OS enforces it. A browser has no equivalent — nothing gates
 * `localStorage` behind a face — so copying that shape would mean keeping a refresh token in
 * storage and «protecting» it with a check that anything reading that storage could simply skip.
 *
 * A passkey moves the proof to where it can actually be checked. The private key never leaves the
 * device's secure hardware, the face unlocks *that*, and the signature it produces is verified by
 * the server before any token exists. Same gesture for the person, a real guarantee underneath.
 *
 * The whole feature degrades to nothing at three separate points, and each one is a normal state
 * rather than an error: a browser without WebAuthn, a device without a platform authenticator, and
 * a deployment whose consumer relying party is not configured.
 */
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { apiRequest, setTokens, type LoginResponse } from '@/shared/api/client';

/**
 * The account this device has enrolled.
 *
 * The ceremony is scoped to one account rather than usernameless, which means the app has to know
 * whose passkey it is holding — the same thing the iOS build stores in `LastEmailStore`. It is an
 * email address the person typed on this device, nothing more; the passkey itself lives in the
 * platform authenticator and is not readable from here.
 */
const ACCOUNT_KEY = 'passkey_account';

/** Set once registration actually completed. Never trusted alone — the server is asked as well. */
const ENROLLED_KEY = 'passkey_enrolled';

/** «Не сейчас» on the offer. Cleared when the account changes, so a new sign-in is asked once. */
const DISMISSED_KEY = 'passkey_dismissed';

/**
 * «Выйти» нажали в этой вкладке — экран входа после этого сам Face ID не открывает.
 *
 * В `sessionStorage`, а не в `localStorage`, намеренно: отметка живёт, пока открыта вкладка или
 * приложение. Человек, который только что вышел, может хотеть войти другим аккаунтом, и системное
 * окно Face ID поперёк экрана ему мешает. А при следующем запуске — сессия кончилась сама,
 * неделя прошла — Face ID открывается сразу: «истекла — прислонился лицом» (владелец, 19.09.2026).
 */
const SIGNED_OUT_KEY = 'tt:signed-out';

/**
 * Every storage access goes through these.
 *
 * Private-mode Safari throws on `localStorage` rather than returning null, and a thrown exception
 * inside a login screen's render is a white page. Failing to remember an account is not worth one.
 */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Nothing to do and nothing to say: the feature simply will not be offered next time.
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // As above.
  }
}

export function rememberedAccount(): string | null {
  return read(ACCOUNT_KEY);
}

/**
 * Called after every successful password sign-in.
 *
 * A different account on the same device wipes the enrolment flags: the passkey that is enrolled
 * belongs to the previous person, and offering «войти по лицу» for an account it cannot open is a
 * button that fails every time.
 */
export function rememberAccount(email: string): void {
  const normalized = email.trim().toLowerCase();
  if (read(ACCOUNT_KEY) !== normalized) {
    drop(ENROLLED_KEY);
    drop(DISMISSED_KEY);
  }
  write(ACCOUNT_KEY, normalized);
}

export function forgetPasskeyEnrolment(): void {
  drop(ENROLLED_KEY);
  drop(DISMISSED_KEY);
}

/** Whether this device believes it finished a registration for the remembered account. */
export function isEnrolledHere(): boolean {
  return read(ENROLLED_KEY) === 'true';
}

export function passkeyOfferDismissed(): boolean {
  return read(DISMISSED_KEY) === 'true';
}

export function markSignedOut(): void {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, 'true');
  } catch {
    // Не запомнили — Face ID просто откроется сам; это не поломка входа.
  }
}

export function clearSignedOut(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // Как выше.
  }
}

/** Нажимали ли «Выйти» в этой вкладке — см. [SIGNED_OUT_KEY]. */
export function signedOutHere(): boolean {
  try {
    return sessionStorage.getItem(SIGNED_OUT_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Человек сам закрыл окно Face ID (или телефон его закрыл, не найдя ключа).
 *
 * Проверка по имени, а не `instanceof DOMException`: `@simplewebauthn/browser` оборачивает ошибку
 * браузера в свой `WebAuthnError extends Error`, сохраняя имя. До 19.09.2026 здесь стояла проверка
 * на `DOMException`, она не срабатывала никогда, и закрытое окно показывалось красной строкой
 * «Не удалось войти по лицу».
 */
export function isPasskeyCancel(cause: unknown): boolean {
  return (
    cause instanceof Error && (cause.name === 'NotAllowedError' || cause.name === 'AbortError')
  );
}

/**
 * Ключ этого аккаунта на этом телефоне УЖЕ есть — заводить второй телефон отказался.
 *
 * Сервер передаёт уже заведённые ключи аккаунта в `excludeCredentials`, и телефон, который
 * находит среди них свой, отвечает `InvalidStateError` (библиотека даёт ему код
 * `ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED`). Это ответ «да, вход по лицу здесь есть», а не
 * отказ. Так бывает на iPhone: у сайта в Safari и у значка на экране «Домой» хранилища разные,
 * а ключ в связке ключей один. До 19.09.2026 такой ответ показывался как «Не получилось», отметка
 * не ставилась, и кнопки «Войти по лицу» в приложении со значка не было никогда.
 */
function isAlreadyOnThisDevice(cause: unknown): boolean {
  if (!(cause instanceof Error)) return false;
  const code = (cause as { code?: unknown }).code;
  return code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED' || cause.name === 'InvalidStateError';
}

export function dismissPasskeyOffer(): void {
  write(DISMISSED_KEY, 'true');
}

/** Whether the server has a consumer relying party at all. Public — asked before anything is offered. */
async function serverSupportsPasskeys(): Promise<boolean> {
  try {
    const { enabled } = await apiRequest<{ enabled: boolean }>(
      '/v1/auth/consumer/webauthn/enabled',
    );
    return enabled;
  } catch {
    return false;
  }
}

/**
 * Can this device do it at all — browser, hardware, and server.
 *
 * All three are checked before any button appears. Opening the system biometric sheet and then
 * failing on a 404 is the worst possible place to discover the feature was never configured.
 */
export async function passkeysAvailable(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;
  // A platform authenticator specifically: a USB security key would technically work, but this is
  // «вход по лицу», and offering it to a laptop with no biometrics promises something else.
  if (!(await platformAuthenticatorIsAvailable())) return false;
  return serverSupportsPasskeys();
}

/**
 * Enrols this device. Requires a live session — the server keys the ceremony on it.
 *
 * Returns `false` for a refusal (the sheet was cancelled, the device declined) rather than
 * throwing: from the screen's point of view «не получилось» and «передумал» lead to the same
 * place, and neither is an error worth a banner.
 */
export async function enrollPasskey(email: string): Promise<boolean> {
  try {
    // The server sends the ceremony options verbatim from `webauthn-rs`; the shape IS the
    // WebAuthn JSON one, so it is typed as that rather than re-declared here.
    const optionsJSON = await apiRequest<PublicKeyCredentialCreationOptionsJSON>(
      '/v1/auth/consumer/webauthn/register-options',
      { method: 'POST' },
    );
    const attestation = await startRegistration({ optionsJSON });
    await apiRequest<void>('/v1/auth/consumer/webauthn/register', {
      method: 'POST',
      body: attestation,
    });
    markEnrolled(email);
    return true;
  } catch (cause) {
    if (isAlreadyOnThisDevice(cause)) {
      markEnrolled(email);
      return true;
    }
    return false;
  }
}

function markEnrolled(email: string): void {
  rememberAccount(email);
  write(ENROLLED_KEY, 'true');
  drop(DISMISSED_KEY);
}

interface LoginOptions {
  /** Round-tripped to `login` — the server keys the pending ceremony on it. */
  challenge_id: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

/**
 * Signs in with the enrolled passkey.
 *
 * Throws on failure, unlike enrolment: this one is the person's *only* action on that screen, and
 * a button that silently does nothing is worse than a message. The caller decides the wording —
 * the errors here are not fit to show as they are.
 */
export async function loginWithPasskey(): Promise<LoginResponse> {
  const account = rememberedAccount();
  if (account === null) throw new Error('нет запомненного аккаунта');

  const { challenge_id, options } = await apiRequest<LoginOptions>(
    '/v1/auth/consumer/webauthn/login-options',
    { method: 'POST', body: { username: account } },
  );

  const assertion = await startAuthentication({ optionsJSON: options });

  const data = await apiRequest<LoginResponse>('/v1/auth/consumer/webauthn/login', {
    method: 'POST',
    body: { challenge_id, credential: assertion },
  });

  setTokens(data.access_token, data.refresh_token);
  write(ENROLLED_KEY, 'true');
  return data;
}

export interface PasskeyCredential {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

export function fetchPasskeys(): Promise<PasskeyCredential[]> {
  return apiRequest<PasskeyCredential[]>('/v1/auth/consumer/webauthn/credentials');
}

/**
 * Removes one enrolled passkey.
 *
 * The local flag goes too — leaving it set would keep offering a Face ID button whose ceremony the
 * server now refuses, which reads as the feature being broken rather than turned off.
 */
export async function deletePasskey(id: string): Promise<void> {
  await apiRequest<void>(`/v1/auth/consumer/webauthn/credentials/${id}`, { method: 'DELETE' });
  forgetPasskeyEnrolment();
}
